import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Layout, { PageHeader } from "../components/Layout";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Plus, Minus, Trash, User, Receipt, Package, Wrench, PaperPlaneTilt, Wallet, LockOpen, ArrowUpRight, ArrowsLeftRight } from "@phosphor-icons/react";
import ReceiptPrint from "../components/ReceiptPrint";
import LabelPrint from "../components/LabelPrint";
import SaleCompleteCard from "../components/SaleCompleteCard";
import AddCustomerModal from "../components/AddCustomerModal";
import CashOutDialog from "../components/CashOutDialog";
import ProductCard from "../components/ProductCard";
import TyroPaymentDialog from "../components/TyroPaymentDialog";
import TradeInIntakeModal from "../components/TradeInIntakeModal";
import { useStore } from "../context/StoreContext";
import { listPendingApprovals, savePendingApproval, clearPendingApproval } from "../lib/tyroPending";

export default function POS() {
  const loc = useLocation();
  const nav = useNavigate();
  const { currentStore } = useStore();
  // Mobile POS tab: "products" or "cart"
  const [mobileTab, setMobileTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // {product_id, sku, name, quantity, unit_price, kind, repair_ticket_id}
  // Inline price-edit state: { idx: <row to edit>, value: <string buffer> }. `null` = not editing.
  const [priceEdit, setPriceEdit] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customers, setCustomers] = useState([]);
  // Map of customer.id -> [open bookings]. Populated on every search hit so
  // the dropdown can flag customers who still have a booking that hasn't
  // been converted (status in [new, confirmed]). Once converted the flag
  // disappears automatically because we filter `open_only=true`.
  const [openBookingsByCustomer, setOpenBookingsByCustomer] = useState({});
  // Open bookings for the currently-selected customer — drives the inline
  // amber banner above the cart.
  const [selectedCustomerOpenBookings, setSelectedCustomerOpenBookings] = useState([]);
  // Store-credit redemption flag — only relevant when a customer is
  // selected AND they have a positive balance. Reset when the customer
  // changes so we don't accidentally redeem on the next sale.
  const [applyStoreCredit, setApplyStoreCredit] = useState(false);
  // Trade-In intake modal trigger — opened from the right-sidebar quick
  // actions so cashiers can buy a used device without leaving POS.
  const [tradeInOpen, setTradeInOpen] = useState(false);
  const [discount, setDiscount] = useState(0);
  // Optional one-off note typed by the cashier at checkout. Surfaces on the
  // sale record + every receipt format (PDF, thermal, public HTML).
  const [saleNote, setSaleNote] = useState("");
  // Stable idempotency key for the current cart. We generate it once per
  // checkout intent and rotate it after a successful sale. Disabled-while-
  // submitting handles fast double-clicks; this key handles the harder case
  // of a network blip where the client never sees the success response and
  // the cashier clicks again. Backend dedupes on (store_id, key) for 30s.
  const idempotencyKey = useRef(`pos-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const rotateIdempotencyKey = () => {
    idempotencyKey.current = `pos-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  };

  // Guards against double-click duplicates. A fast cashier clicking
  // "Complete Sale" three times used to create three identical invoices
  // (real prod bug — S-000462/S-000463 collision). We disable the button
  // for the full duration of the POST + post-sale plumbing.
  const [submitting, setSubmitting] = useState(false);
  // Synchronous in-flight lock. `submitting` state is async — a fast
  // double-click can fire two onClick handlers BEFORE React commits the
  // first setSubmitting(true). The ref gives us a synchronous check that
  // doesn't rely on React's render schedule, fully eliminating the race
  // even when the cashier double-taps faster than the UI can react.
  const checkoutInFlight = useRef(false);
  const invoicePayInFlight = useRef(false);
  const invoicePayIdem = useRef("");
  // Tyro post-charge recovery: remember the last approved Tyro meta so a
  // failed sale-save can be retried (idempotently — same key, no re-charge),
  // and surface the error inside the Tyro dialog instead of hanging.
  const lastTyroMeta = useRef(null);
  const [tyroSaveError, setTyroSaveError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  // Split-tender state — when active, the single amount-tendered input is
  // replaced by per-method amount fields (Cash + Card). Auto-balance: if the
  // user fills one side, the remaining-to-total appears on the other.
  const [splitMode, setSplitMode] = useState(false);
  const [splitCash, setSplitCash] = useState("");
  const [splitCard, setSplitCard] = useState("");
  const [splitTyro, setSplitTyro] = useState("");
  const [splitOther, setSplitOther] = useState("");
  const [lastSale, setLastSale] = useState(null);
  const [ticketQ, setTicketQ] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  // Autocomplete for Repair pickup / Settle invoice: matches partial ticket or
  // invoice numbers (substring, so last-digit-match works: "45" finds R-00045).
  const [suggestions, setSuggestions] = useState([]); // [{kind:'ticket'|'invoice', ...}]
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestHighlight, setSuggestHighlight] = useState(0);
  // Invoice balance settlement — opened when the "Repair pickup" input detects
  // an invoice number (e.g. "S-000055") instead of a ticket number.
  const [invoicePay, setInvoicePay] = useState({
    open: false,
    invoice: null,
    amount: "",
    method: "cash",
    submitting: false,
  });
  const [postSaleOpen, setPostSaleOpen] = useState(false);
  // Tyro approvals that were charged on the terminal but never confirmed by
  // the server (tab closed / crash / network drop mid-save).
  const [orphanApprovals, setOrphanApprovals] = useState([]);
  const [recovering, setRecovering] = useState(false);
  const [tyroOpen, setTyroOpen] = useState(false);   // Tyro EFTPOS payment dialog (single-tender)
  const [tyroSplitOpen, setTyroSplitOpen] = useState(false);  // Tyro for the card slice of a split tender
  const [tyroInvOpen, setTyroInvOpen] = useState(false);  // Tyro for invoice settle
  const [lastSaleCustomer, setLastSaleCustomer] = useState(null);
  const [lastSaleTicket, setLastSaleTicket] = useState(null); // repair ticket linked to sale (for label print)
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [printPrefs, setPrintPrefs] = useState(null); // { receipt_printer_id, auto_print_receipt, ... }
  const [printConfigured, setPrintConfigured] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { setOrphanApprovals(listPendingApprovals()); }, []);
  // Load PrintNode status + per-store prefs. Re-fetch when the active store changes.
  useEffect(() => {
    api.get("/print/status").then((r) => setPrintConfigured(!!r.data?.configured)).catch(() => setPrintConfigured(false));
  }, []);
  useEffect(() => {
    if (!currentStore?.id) return;
    api.get("/users/me/print-prefs").then((r) => setPrintPrefs(r.data)).catch(() => setPrintPrefs(null));
  }, [currentStore?.id]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (customerSearch.length >= 1) {
        api.get("/customers", { params: { q: customerSearch } }).then(async (r) => {
          const hits = r.data.slice(0, 8);
          setCustomers(hits);
          // Fetch open bookings for every search-hit customer in parallel
          // so we can flag them inline. Soft-fails per row — a 4xx on one
          // doesn't poison the whole dropdown.
          const ids = hits.map((c) => c.id).filter(Boolean);
          if (ids.length === 0) return setOpenBookingsByCustomer({});
          const results = await Promise.allSettled(
            ids.map((cid) =>
              api.get("/bookings", { params: { customer_id: cid, open_only: true } })
            )
          );
          const map = {};
          ids.forEach((cid, idx) => {
            const r2 = results[idx];
            if (r2.status === "fulfilled") map[cid] = r2.value.data || [];
          });
          setOpenBookingsByCustomer(map);
        });
      } else { setCustomers([]); setOpenBookingsByCustomer({}); }
    }, 200);
    return () => clearTimeout(t);
  }, [customerSearch]);

  // Whenever the cashier picks a customer, fetch THEIR unconverted bookings
  // so the cart can render a banner ("Convert this booking now"). The list
  // auto-clears when the customer is changed or the booking is converted.
  useEffect(() => {
    if (!customer?.id) { setSelectedCustomerOpenBookings([]); return; }
    api.get("/bookings", {
      params: { customer_id: customer.id, open_only: true },
    })
      .then((r) => setSelectedCustomerOpenBookings(r.data || []))
      .catch(() => setSelectedCustomerOpenBookings([]));
  }, [customer?.id]);

  // Debounced autocomplete for the Repair-pickup / Invoice-settle input.
  // Matches any substring of the ticket or invoice number so staff can type
  // "45" and pull up "R-00045" or "S-000045" without keying the full number.
  useEffect(() => {
    const q = ticketQ.trim();
    if (q.length < 1) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const [tickRes, invRes, tiRes] = await Promise.allSettled([
          api.get("/repair-tickets", { params: { q } }),
          api.get("/sales", { params: { q, limit: 20 } }),
          // Trade-ins: only useful when user types T or TI…
          q.length >= 1 && q.toUpperCase().includes("T")
            ? api.get("/trade-ins", { params: { q, limit: 8 } })
            : Promise.resolve({ data: { items: [] } }),
        ]);
        const needle = q.toLowerCase();
        const tickHits = (tickRes.status === "fulfilled" ? tickRes.value.data : [])
          .filter((t2) => (t2.ticket_number || "").toLowerCase().includes(needle))
          .slice(0, 5)
          .map((t2) => ({
            kind: "ticket",
            id: t2.id,
            label: t2.ticket_number,
            meta: `${t2.device_make || ""} ${t2.device_model || ""}`.trim() || "Repair",
            customer: t2.customer_name || "—",
            amount: Number(t2.final_cost > 0 ? t2.final_cost : t2.estimated_cost || 0),
            status: t2.status,
            raw: t2,
          }));
        const invHits = (invRes.status === "fulfilled" ? invRes.value.data : [])
          .filter((s) => (s.sale_number || "").toLowerCase().includes(needle))
          .slice(0, 5)
          .map((s) => {
            const itemCount = (s.items || s.line_items || []).length;
            const balance = Number(s.balance_due != null
              ? s.balance_due
              : Math.max(0, Number(s.total || 0) - Number(s.amount_tendered || 0)));
            return {
              kind: "invoice",
              id: s.id,
              label: s.sale_number,
              meta: `${itemCount} item${itemCount === 1 ? "" : "s"}`,
              customer: s.customer_name || "Walk-in",
              amount: balance,
              voided: !!s.voided,
              raw: s,
            };
          });
        const combined = [...tickHits, ...invHits];

        // Trade-In hits (only in_stock units — sold ones aren't sellable).
        const tiPayload = tiRes.status === "fulfilled" ? (tiRes.value.data?.items || []) : [];
        const tiHits = tiPayload
          .filter((ti) => ti.status === "in_stock")
          .filter((ti) => (ti.trade_in_number || "").toLowerCase().includes(needle))
          .slice(0, 4)
          .map((ti) => ({
            kind: "trade_in",
            id: ti.id,
            label: ti.trade_in_number,
            meta: `${ti.device_make || ""} ${ti.device_model || ""} ${ti.storage || ""}`.trim() || "Trade-in",
            customer: ti.customer_name_snapshot || "—",
            amount: Number(ti.sell_price || 0),
            raw: ti,
          }));
        const finalCombined = [...tiHits, ...combined].slice(0, 10);
        setSuggestions(finalCombined);
        setSuggestHighlight(0);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [ticketQ]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)
    );
  }, [products, search]);

  const addToCart = (p) => {
    setCart((c) => {
      const existing = c.find((x) => x.product_id === p.id);
      if (existing) {
        if (existing.quantity + 1 > p.stock) {
          toast.error(`Only ${p.stock} in stock`);
          return c;
        }
        return c.map((x) => x.product_id === p.id ? { ...x, quantity: x.quantity + 1 } : x);
      }
      if (p.stock <= 0) {
        toast.error("Out of stock");
        return c;
      }
      return [...c, { product_id: p.id, sku: p.sku, name: p.name, quantity: 1, unit_price: p.price, kind: "product" }];
    });
  };

  const changeQty = (idx, delta) => {
    setCart((c) => c.map((x, i) => {
      if (i !== idx) return x;
      const next = x.quantity + delta;
      if (next <= 0) return null;
      if (x.kind === "product" && x.product_id) {
        const prod = products.find((p) => p.id === x.product_id);
        if (prod && next > prod.stock) { toast.error(`Only ${prod.stock} in stock`); return x; }
      }
      return { ...x, quantity: next };
    }).filter(Boolean));
  };

  const removeItem = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  /** Commit the price buffer from `priceEdit` back to the cart.
   *  Blank / negative / NaN inputs are ignored (buffer cleared, row keeps its original price).
   *  Stock availability for inventory items isn't validated at edit time — the cart trusts
   *  the cashier's override; totals recalculate automatically from `unit_price * quantity`.
   */
  const commitPriceEdit = () => {
    if (!priceEdit) return;
    const { idx, value } = priceEdit;
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) {
      setCart((c) => c.map((it, i) => (i === idx ? { ...it, unit_price: Math.round(n * 100) / 100 } : it)));
    }
    setPriceEdit(null);
  };

  /** Try to resolve the user's input as an INVOICE number first (e.g. "S-000055").
   * We use the `GET /api/sales` list endpoint with a `q` param; the server already
   * supports searching invoices by `sale_number`. Returns the matched sale or null.
   */
  const lookupInvoice = async (num) => {
    try {
      const { data } = await api.get("/sales", { params: { q: num, limit: 20 } });
      const hit = data.find((s) => (s.sale_number || "").toLowerCase() === num.toLowerCase())
               || data.find((s) => (s.sale_number || "").toLowerCase().endsWith(num.toLowerCase()));
      return hit || null;
    } catch {
      return null;
    }
  };

  // Open the invoice-settle modal for a given sale object (used by both the
  // autocomplete click path and the existing form-submit fallback).
  const openInvoiceSettle = (inv) => {
    const balance = Math.max(0, (inv.total || 0) - (inv.amount_tendered || 0));
    if (inv.voided) { toast.error(`Invoice ${inv.sale_number} is voided`); return false; }
    if (balance <= 0.005) { toast.success(`Invoice ${inv.sale_number} is already fully paid`); return false; }
    setInvoicePay({ open: true, invoice: inv, amount: balance.toFixed(2), method: "cash", submitting: false });
    setTicketQ("");
    setSuggestions([]);
    setSuggestOpen(false);
    return true;
  };

  const addTicketOrInvoice = async (input) => {
    const num = (input || "").trim();
    if (!num) return;
    setLookingUp(true);
    try {
      const upper = num.toUpperCase();
      // ===== Trade-In pickup (TI-XXXXX) =====
      // Trade-ins are saleable Products under the hood (kind=trade_in,
      // SKU=TI-XXXXX) — we look them up by trade-in number and push the
      // linked product into the cart at its current sell price.
      if (upper.startsWith("TI-") || upper.startsWith("TI")) {
        await addTradeInToCart(upper);
        return;
      }
      const firstLetter = num.charAt(0).toLowerCase();
      if (firstLetter === "s") {
        const inv = await lookupInvoice(num);
        if (!inv) {
          toast.error(`No invoice found for "${num}"`);
          return;
        }
        openInvoiceSettle(inv);
        return;
      }
      // ===== Repair ticket pickup (original flow) =====
      await addTicketToCart(num);
    } finally {
      setLookingUp(false);
    }
  };

  /** Look up a trade-in by TI-XXXXX and push its linked Product into the
   *  cart. Trade-ins are unique units so we always add quantity=1 and
   *  refuse duplicates (the product has stock=1 so the regular cart
   *  guard would catch this anyway, but failing fast here gives a
   *  clearer error message). */
  const addTradeInToCart = async (tiNumber) => {
    try {
      const r = await api.get(`/trade-ins/by-number/${encodeURIComponent(tiNumber)}`);
      const ti = r.data;
      if (ti.status !== "in_stock") {
        toast.error(`${ti.trade_in_number} is ${ti.status} — cannot ring up`);
        return;
      }
      if (!ti.product_id) {
        toast.error(`${ti.trade_in_number} has no linked product`);
        return;
      }
      // Find the product in the local catalog (already loaded). If not
      // present (the main grid hides trade-in products by default), do a
      // dedicated fetch that includes them.
      let prod = products.find((p) => p.id === ti.product_id);
      if (!prod) {
        const all = await api
          .get("/products", { params: { include_trade_ins: true } })
          .then((x) => x.data);
        prod = all.find((p) => p.id === ti.product_id);
      }
      if (!prod) {
        toast.error(`Trade-in product missing from catalog`);
        return;
      }
      if (cart.some((c) => c.product_id === prod.id)) {
        toast.error(`${ti.trade_in_number} is already in the cart`);
        return;
      }
      addToCart(prod);
      setTicketQ("");
      setSuggestions([]);
      setSuggestOpen(false);
      toast.success(`Added ${ti.trade_in_number} · ${ti.device_make} ${ti.device_model}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.error(`No trade-in found for "${tiNumber}"`);
      else toast.error(formatApiError(e));
    }
  };

  // Click handler for an autocomplete suggestion — routes directly to cart add
  // (ticket) or invoice-settle modal (invoice) without re-querying the server.
  const pickSuggestion = (s) => {
    if (!s) return;
    setSuggestOpen(false);
    if (s.kind === "invoice") {
      openInvoiceSettle(s.raw);
    } else if (s.kind === "trade_in") {
      addTradeInToCart(s.label);
    } else {
      addTicketToCart(s.label);
    }
  };

  const addTicketToCart = async (ticketNumber) => {
    const num = (ticketNumber || "").trim();
    if (!num) return;
    try {
      // Fetch list and match by exact ticket_number (case-insensitive)
      const { data } = await api.get("/repair-tickets", { params: { q: num } });
      const hit = data.find((t) => t.ticket_number.toLowerCase() === num.toLowerCase())
                || data.find((t) => t.ticket_number.toLowerCase().endsWith(num.toLowerCase()))
                || data[0];
      if (!hit) {
        toast.error(`No ticket found for "${num}"`);
        return;
      }
      if (hit.status === "picked_up") {
        toast.error(`Ticket ${hit.ticket_number} is already paid and picked up`);
        return;
      }
      if (cart.some((x) => x.repair_ticket_id === hit.id)) {
        toast.error(`Ticket ${hit.ticket_number} is already in the cart`);
        return;
      }
      // A repair can only ever carry ONE invoice. If a sale already exists,
      // route to settling it (balance owing) or refuse (already paid) —
      // the backend hard-blocks a second sale anyway, so catch it here with
      // a clearer message than a failed checkout.
      try {
        const { data: linked } = await api.get(`/repair-tickets/${hit.id}/linked-sale`);
        if (linked?.sale && !linked.sale.voided) {
          if (linked.balance_due > 0.005) {
            setInvoicePay({
              open: true,
              invoice: linked.sale,
              amount: linked.balance_due.toFixed(2),
              method: "cash",
              submitting: false,
            });
            setTicketQ("");
            toast.info(`${hit.ticket_number} is already on ${linked.sale.sale_number} — settling $${money(linked.balance_due)}`);
          } else {
            toast.error(`${hit.ticket_number} is already invoiced and paid on ${linked.sale.sale_number}`);
          }
          return;
        }
      } catch { /* no linked sale — normal new-sale path */ }
      const dueBalance = Number(hit.final_cost && hit.final_cost > 0 ? hit.final_cost : hit.estimated_cost) || 0;
      const ticketAccessories = Array.isArray(hit.pending_accessories) ? hit.pending_accessories : [];
      setCart((c) => [
        ...c,
        {
          product_id: null,
          sku: hit.ticket_number,
          name: `Repair · ${hit.device_make} ${hit.device_model}`,
          quantity: 1,
          unit_price: dueBalance,
          kind: "repair",
          repair_ticket_id: hit.id,
          ticket_number: hit.ticket_number,
        },
        // Auto-add accessories captured on the intake ticket so the
        // cashier isn't forced to re-key case / screen protector / etc.
        // They can still be edited or removed from the cart before checkout.
        ...ticketAccessories.map((a) => ({
          product_id: a.product_id || null,
          sku: a.sku || "",
          name: a.name,
          quantity: Number(a.quantity || 1),
          unit_price: Number(a.unit_price || 0),
          kind: a.kind || "product",
          repair_ticket_id: null,
        })),
      ]);
      // Auto-attach customer to the sale if not yet chosen
      if (!customer && hit.customer_id) {
        try {
          const { data: c } = await api.get(`/customers/${hit.customer_id}`);
          setCustomer(c);
        } catch { /* ignore */ }
      }
      setTicketQ("");
      const accToast = ticketAccessories.length > 0
        ? ` + ${ticketAccessories.length} accessor${ticketAccessories.length === 1 ? "y" : "ies"}`
        : "";
      toast.success(`${hit.ticket_number} added · due $${money(dueBalance)}${accToast}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  /** Submit a top-up payment against the invoice selected in `invoicePay`. */
  const submitInvoicePayment = async (tyroMeta = null) => {
    // React invokes a directly-bound onClick handler with its click event.
    // This function also receives genuine Tyro metadata after terminal
    // approval, so explicitly discard any browser event before building the
    // JSON request body. Without this guard JSON.stringify follows React's
    // circular fiber references and cash/card payments fail too.
    const isClickEvent = tyroMeta && (
      typeof tyroMeta.preventDefault === "function"
      || "nativeEvent" in tyroMeta
      || "currentTarget" in tyroMeta
    );
    const approvedTyroMeta = isClickEvent ? null : tyroMeta;
    const inv = invoicePay.invoice;
    if (!inv) return;
    const amt = Number(invoicePay.amount || 0);
    const balance = Math.max(0, (inv.total || 0) - (inv.amount_tendered || 0));
    if (!(amt > 0)) return toast.error("Enter a payment amount");
    if (amt > balance + 0.005) return toast.error(`Max $${money(balance)} remaining on ${inv.sale_number}`);
    // Tyro: hand off to EFTPOS terminal dialog
    if (invoicePay.method === "tyro" && !approvedTyroMeta) {
      setTyroInvOpen(true);
      return;
    }
    if (invoicePayInFlight.current) return;
    invoicePayInFlight.current = true;
    setInvoicePay((p) => ({ ...p, submitting: true }));
    try {
      // One key per settlement attempt, replayed on retry — a double-clicked
      // button or a dropped response can never append the tender twice.
      if (!invoicePayIdem.current) invoicePayIdem.current = `bal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const payload = { amount: amt, method: invoicePay.method, idempotency_key: invoicePayIdem.current };
      if (approvedTyroMeta) payload.tyro_meta = approvedTyroMeta;
      const pendingId = approvedTyroMeta ? `pay-${invoicePayIdem.current}` : null;
      if (pendingId) {
        savePendingApproval({
          id: pendingId,
          kind: "sale_payment",
          sale_id: inv.id,
          sale_number: inv.sale_number || "",
          payload,
          amount: approvedTyroMeta.transaction_amount || amt,
          tyro_ref: approvedTyroMeta.transaction_reference || "",
          auth_code: approvedTyroMeta.authorisation_code || "",
        });
      }
      await api.post(`/sales/${inv.id}/payments`, payload);
      if (pendingId) {
        clearPendingApproval(pendingId);
        setOrphanApprovals(listPendingApprovals());
      }
      const remaining = Math.max(0, balance - amt);
      toast.success(
        remaining > 0.005
          ? `$${money(amt)} applied · $${money(remaining)} still due on ${inv.sale_number}`
          : `${inv.sale_number} fully settled · $${money(amt)} collected`
      );
      invoicePayIdem.current = "";
      setInvoicePay({ open: false, invoice: null, amount: "", method: "cash", submitting: false });
    } catch (e) {
      toast.error(formatApiError(e));
      setInvoicePay((p) => ({ ...p, submitting: false }));
      if (approvedTyroMeta) throw e; // keep the Tyro dialog up with Retry save
    } finally {
      invoicePayInFlight.current = false;
    }
  };

  // Handle ?ticket=R-00007 query param (deep-link from ticket detail).
  // Guarded with a ref because React 18 StrictMode double-invokes effects in
  // dev — without this guard, the ticket was being added to the cart twice.
  const ticketDeepLinkHandled = useRef(false);
  useEffect(() => {
    if (ticketDeepLinkHandled.current) return;
    const params = new URLSearchParams(loc.search);
    const t = params.get("ticket");
    if (!t) return;
    ticketDeepLinkHandled.current = true;
    // Resolve the ticket first: if it's a deposit (linked sale w/ balance due),
    // open the invoice-settlement flow so we top up the existing sale rather
    // than double-charging via a brand-new sale.
    (async () => {
      try {
        const list = await api.get("/repair-tickets", { params: { q: t } });
        const hit = list.data.find((x) => x.ticket_number.toLowerCase() === t.toLowerCase()) || list.data[0];
        if (hit) {
          const { data: linked } = await api.get(`/repair-tickets/${hit.id}/linked-sale`);
          if (linked?.sale && linked.balance_due > 0.005 && !linked.sale.voided) {
            setInvoicePay({
              open: true,
              invoice: linked.sale,
              amount: linked.balance_due.toFixed(2),
              method: "cash",
              submitting: false,
            });
            if (!customer && hit.customer_id) {
              try {
                const { data: c } = await api.get(`/customers/${hit.customer_id}`);
                setCustomer(c);
              } catch { /* ignore */ }
            }
            toast.success(`Balance due $${money(linked.balance_due)} on ${linked.sale.sale_number}`);
            nav("/pos", { replace: true });
            return;
          }
        }
      } catch { /* fall through to normal add-to-cart */ }
      addTicketToCart(t);
      nav("/pos", { replace: true });
    })();
    // eslint-disable-next-line
  }, []);

  const subtotal = cart.reduce((s, x) => s + x.unit_price * x.quantity, 0);
  const taxRate = Number(currentStore?.tax_rate || 0) / 100;
  const taxInclusive = currentStore?.tax_inclusive !== false; // default to true (AU/NZ)

  // If tax-inclusive: prices already contain tax. Subtotal shown is inclusive.
  //   Tax portion = subtotal - (subtotal / (1 + rate))
  //   Total = subtotal - discount
  // If tax-exclusive: tax is added on top after discount.
  //   Tax = (subtotal - discount) * rate
  //   Total = (subtotal - discount) + tax
  const discountNum = Number(discount || 0);
  const afterDiscount = Math.max(0, subtotal - discountNum);
  const taxAmount = taxRate > 0
    ? (taxInclusive
        ? afterDiscount - (afterDiscount / (1 + taxRate))
        : afterDiscount * taxRate)
    : 0;
  const preCreditTotal = taxInclusive
    ? afterDiscount
    : afterDiscount + taxAmount;
  const creditBalance = Number(customer?.store_credit_balance || 0);
  // Mirrors the server: redemption = min(balance, total) and comes off the amount to pay.
  const creditApplied = applyStoreCredit && customer?.id
    ? Math.round(Math.min(creditBalance, preCreditTotal) * 100) / 100
    : 0;
  const total = Math.max(0, Math.round((preCreditTotal - creditApplied) * 100) / 100);
  // Split-tender numbers — when split mode is on, summing the per-method
  // amounts is the source of truth; otherwise we use the single `tendered`
  // input. Both paths feed the same downstream checkout flow.
  const splitCashNum = Number(splitCash || 0);
  const splitCardNum = Number(splitCard || 0);
  const splitTyroNum = Number(splitTyro || 0);
  const splitOtherNum = Number(splitOther || 0);
  const splitTotal = splitMode ? (splitCashNum + splitCardNum + splitTyroNum + splitOtherNum) : 0;
  const tenderedNum = splitMode ? splitTotal : Number(tendered || 0);
  // Round both sides to cents to avoid JS float-math traps (e.g. 49.95 vs 49.950000000001)
  const totalCents = Math.round(total * 100);
  const tenderedCents = Math.round(tenderedNum * 100);
  const shortOnTender = tenderedCents < totalCents;
  const change = Math.max(0, (tenderedCents - totalCents) / 100);
  const balanceDue = Math.max(0, (totalCents - tenderedCents) / 100);
  const isDeposit = shortOnTender && tenderedCents > 0;
  // Single-tender Tyro charges the typed amount when it's a part-payment, otherwise the full total.
  const tyroChargeAmount = !splitMode && isDeposit ? tenderedNum : total;

  // Server-side cart check BEFORE any terminal charge — a declined save after
  // an approved Tyro payment leaves the customer charged with no sale.
  const precheckCart = async () => {
    try {
      await api.post("/sales/precheck", {
        items: cart.map((x) => ({ product_id: x.product_id, name: x.name, quantity: x.quantity, kind: x.kind, repair_ticket_id: x.repair_ticket_id || null })),
      });
      return true;
    } catch (e) {
      toast.error(`${formatApiError(e)} — nothing was charged`);
      return false;
    }
  };

  const checkout = async (tyroMeta = null) => {
    if (cart.length === 0) return toast.error("Cart is empty");
    // Synchronous early-out: if a checkout is already in-flight (or a
    // Tyro dialog has been kicked off but not yet completed), drop the
    // click. Without this an impatient cashier can re-open the Tyro
    // dialog stack mid-transaction.
    if (checkoutInFlight.current) return;

    // Split-tender with Tyro: if the user has typed a non-zero amount into
    // the Tyro row, the Tyro portion MUST go through the real SDK before we
    // can record the sale. Open the dialog for just the Tyro slice; the
    // onApproved callback will re-enter this function with tyroMeta set and
    // the split state intact. Cash-first per user preference — the split
    // totals include cash and we only charge Tyro for the balance-less-cash.
    if (!tyroMeta && splitMode && splitTyroNum > 0) {
      // Allow partial-split (deposit) — only block overpayment, not
      // under-payment. Required by cert `Retail.SplitPurchase.1` where
      // step 1 takes a $112.50 deposit ($10 cash + $102.50 Tyro) on a
      // $215 sale, then a second Tyro $102.50 on Sale Detail clears
      // the balance.
      if (splitTotal > total + 0.01) {
        return toast.error(`Split tender ($${splitTotal.toFixed(2)}) exceeds total ($${total.toFixed(2)})`);
      }
      // Lock the checkout entry-point so a second click while the Tyro
      // dialog is open can't double-stack the EFTPOS transaction. The
      // dialog's onClose / onApproved callbacks both clear the lock.
      checkoutInFlight.current = true;
      if (!(await precheckCart())) { checkoutInFlight.current = false; return; }
      setTyroSplitOpen(true);
      return;
    }

    if (total > 0 && tenderedCents <= 0 && !tyroMeta && !splitMode) return toast.error("Enter a tendered amount (or the exact total)");
    if (isDeposit && !tyroMeta && !splitMode) {
      const ok = window.confirm(
        `Take a deposit of $${tenderedNum.toFixed(2)}?\n\nBalance due: $${balanceDue.toFixed(2)}\n\nThe customer can settle the balance later from the invoice page.`
      );
      if (!ok) return;
    }
    // Guard: if an in-flight POST is already running, ignore further clicks.
    // The button is disabled while submitting=true but defensive double-check
    // via both React state AND a synchronous ref (state is async; ref isn't).
    if (submitting || checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    setSubmitting(true);
    if (tyroMeta) { lastTyroMeta.current = tyroMeta; setTyroSaveError(null); }
    try {
      const payload = {
        items: cart.map((x) => ({
          product_id: x.product_id,
          sku: x.sku,
          name: x.name,
          quantity: x.quantity,
          unit_price: x.unit_price,
          line_total: x.unit_price * x.quantity,
          kind: x.kind,
          repair_ticket_id: x.repair_ticket_id || null,
        })),
        discount: Number(discount || 0),
        tax: Number(taxAmount.toFixed(2)),
        payment_method: tyroMeta && !splitMode ? "card_manual" : paymentMethod,
        // Tyro pays the FULL total in one shot — even on what would otherwise
        // be a "deposit" tender. Use the live total as the tendered amount.
        amount_tendered: tyroMeta && !splitMode ? tyroChargeAmount : tenderedNum,
        customer_id: customer?.id || null,
        // Auto-apply store credit if the cashier ticked the checkbox on the
        // customer card. Server caps redemption at min(balance, total).
        apply_store_credit: !!(applyStoreCredit && customer?.id),
        note: saleNote.trim(),
      };
      if (tyroMeta && !splitMode) {
        payload.tyro_meta = tyroMeta;
      }
      // Split-tender: send the granular `payments[]` array. Backend ignores
      // `payment_method` + `amount_tendered` when `payments` is non-empty
      // and uses the array as the source of truth. If tyroMeta is present
      // (we just approved the Tyro slice), attach it to its row so the
      // resulting Sale gets linked to that terminal transaction for refunds.
      if (splitMode) {
        const split = [];
        if (splitCashNum > 0) split.push({ amount: splitCashNum, method: "cash" });
        if (splitCardNum > 0) split.push({ amount: splitCardNum, method: "card_manual" });
        if (splitTyroNum > 0) {
          const row = { amount: splitTyroNum, method: "tyro" };
          if (tyroMeta) row.tyro_meta = tyroMeta;
          split.push(row);
        }
        if (splitOtherNum > 0) split.push({ amount: splitOtherNum, method: "other" });
        if (split.length > 0) payload.payments = split;
      }
      // Durability: the card is already charged, so persist the exact request
      // before sending it. If this tab dies mid-flight the POS replays it on
      // next load with the same Idempotency-Key (server returns the existing
      // sale if it actually landed).
      const pendingId = tyroMeta ? `sale-${idempotencyKey.current}` : null;
      if (pendingId) {
        savePendingApproval({
          id: pendingId,
          kind: "sale",
          payload,
          idempotency_key: idempotencyKey.current,
          amount: tyroMeta.transaction_amount || tyroChargeAmount,
          tyro_ref: tyroMeta.transaction_reference || "",
          auth_code: tyroMeta.authorisation_code || "",
        });
      }
      const { data } = await api.post("/sales", payload, {
        // Idempotency-Key prevents server-side duplicate creation on
        // double-click / network retry. Same key sent on retries so the
        // backend returns the first sale instead of creating a second.
        headers: { "Idempotency-Key": idempotencyKey.current },
      });
      if (pendingId) {
        clearPendingApproval(pendingId);
        setOrphanApprovals(listPendingApprovals());
      }
      setLastSale(data);
      setLastSaleCustomer(customer);

      // If the sale includes a repair ticket line, fetch the ticket for label printing
      const ticketItem = (data.items || []).find((it) => it.repair_ticket_id);
      if (ticketItem) {
        try {
          const { data: ticket } = await api.get(`/repair-tickets/${ticketItem.repair_ticket_id}`);
          setLastSaleTicket(ticket);
        } catch { setLastSaleTicket(null); }
      } else {
        setLastSaleTicket(null);
      }

      setCart([]);
      setDiscount(0);
      setTendered("");
      setSplitMode(false);
      setSplitCash(""); setSplitCard(""); setSplitTyro(""); setSplitOther("");
      setCustomer(null);
      setCustomerSearch("");
      setApplyStoreCredit(false);
      setSaleNote("");
      rotateIdempotencyKey(); // fresh key for the next sale
      loadProducts();
      toast.success(`Sale ${data.sale_number} rung up · $${money(data.total)}`);

      // ---- Auto cloud-print receipt (per-store setting) ----
      if (printConfigured && printPrefs?.auto_print_receipt && printPrefs?.receipt_printer_id) {
        try {
          await api.post(`/print/receipt/${data.id}`, {
            printer_id: printPrefs.receipt_printer_id,
            copies: 1,
          });
          toast.success("Receipt sent to printer");
        } catch (err) {
          toast.error("Auto-print failed: " + formatApiError(err));
        }
      }

      // For Tyro flows the TyroPaymentDialog stays open and morphs
      // its body into the SaleCompleteCard action panel — no separate
      // pop-up needed. For cash / manual card / other / split-without-
      // Tyro we open the unified card as a standalone overlay.
      if (!tyroMeta) {
        setPostSaleOpen(true);
      }
    } catch (e) {
      // Card already charged: transient failures (network / 5xx) are thrown
      // back to the Tyro dialog, which retries automatically with the same
      // idempotency key. Only a hard rejection shows the recovery panel.
      const st = e?.response?.status;
      if (tyroMeta && (!st || st >= 500)) throw e;
      toast.error(formatApiError(e));
      if (tyroMeta) setTyroSaveError(formatApiError(e) || "Saving the sale failed");
    } finally {
      checkoutInFlight.current = false;
      setSubmitting(false);
    }
  };

  // Replay one unrecorded Tyro approval. Safe to run even if the original
  // request DID land — both endpoints dedupe on the same idempotency key.
  const recoverApproval = async (entry) => {
    setRecovering(true);
    try {
      if (entry.kind === "sale_payment") {
        await api.post(`/sales/${entry.sale_id}/payments`, entry.payload);
        toast.success(`Tyro $${money(entry.amount)} recorded on ${entry.sale_number || "the invoice"}`);
      } else {
        const { data } = await api.post("/sales", entry.payload, {
          headers: { "Idempotency-Key": entry.idempotency_key },
        });
        toast.success(`Recovered — sale ${data.sale_number} recorded ($${money(data.total)})`);
      }
      clearPendingApproval(entry.id);
      setOrphanApprovals(listPendingApprovals());
      loadProducts();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't record that payment — try again");
    } finally {
      setRecovering(false);
    }
  };

  const discardApproval = (entry) => {
    if (!window.confirm(`Discard the approved Tyro payment of $${money(entry.amount)}? The customer was charged — only do this if you've already recorded or refunded it.`)) return;
    clearPendingApproval(entry.id);
    setOrphanApprovals(listPendingApprovals());
  };

  const retryTyroSave = () => {
    if (!lastTyroMeta.current) return;
    setTyroSaveError(null);
    checkout(lastTyroMeta.current);
  };

  const createWalkIn = () => setAddCustomerOpen(true);

  const printReceipt = async () => {
    // If a cloud receipt printer is configured, use it regardless of the auto-print toggle —
    // the user explicitly tapped Print, so we honour that intent. Fall back to browser dialog.
    if (printConfigured && printPrefs?.receipt_printer_id && lastSale?.id) {
      try {
        await api.post(`/print/receipt/${lastSale.id}`, {
          printer_id: printPrefs.receipt_printer_id,
          copies: 1,
        });
        toast.success("Receipt sent to printer");
        return;
      } catch (err) {
        toast.error("Cloud print failed — opening browser print dialog");
      }
    }
    document.body.classList.remove("print-label");
    window.print();
  };

  const printLabel = async () => {
    if (!lastSaleTicket) return;
    if (printConfigured && printPrefs?.label_printer_id) {
      try {
        await api.post(`/print/label/${lastSaleTicket.id}`, {
          printer_id: printPrefs.label_printer_id,
          copies: 1,
        });
        toast.success(`Label sent to printer · ${lastSaleTicket.ticket_number}`);
        return;
      } catch (err) {
        toast.error("Cloud print failed — opening browser print dialog");
      }
    }
    document.body.classList.add("print-label");
    window.print();
    setTimeout(() => document.body.classList.remove("print-label"), 500);
  };

  return (
    <Layout>
      <div className="grid grid-cols-1 md:grid-cols-12 min-h-screen bg-[#FFFFFF]">
        {/* Left: Product catalog — on mobile, hidden when cart tab is active */}
        <section className={`md:col-span-7 lg:col-span-7 xl:col-span-8 p-3 sm:p-4 md:p-5 lg:p-6 min-w-0 ${mobileTab === "cart" ? "hidden md:block" : ""}`}>
          <div className="flex items-center justify-between gap-3 mb-4 sm:mb-5 flex-wrap">
            <div>
              <div className="overline mb-1 sm:mb-1.5">Point of sale</div>
              <h1 className="font-display font-semibold text-[22px] sm:text-[26px] md:text-[28px] lg:text-[32px] tracking-tight leading-none text-[#121216]">Checkout</h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <TillStatusPill />
              <div className="relative w-full sm:w-80 md:w-64 lg:w-96 max-w-full">
                <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input
                  data-testid="pos-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search SKU, name, category"
                  className="w-full h-12 pl-11 pr-4 rounded-xl border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 text-[14px] bg-white transition-all"
                />
              </div>
            </div>
          </div>

          {/* Unrecorded Tyro approvals — the terminal took the money but the
              POS never confirmed the sale (tab closed / crash / network drop). */}
          {orphanApprovals.length > 0 && (
            <div className="mb-4 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="tyro-recovery-banner">
              <div className="text-[13px] font-bold text-amber-900">
                {orphanApprovals.length} Tyro payment{orphanApprovals.length === 1 ? "" : "s"} approved on the terminal but not recorded
              </div>
              {orphanApprovals.map((entry) => (
                <div key={entry.id} className="flex items-center justify-between gap-3 flex-wrap bg-white border border-amber-200 rounded-xl px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-[14px] font-bold text-[#121216] font-mono tabular-nums">
                      ${money(entry.amount)} approved
                    </div>
                    <div className="text-[11px] text-[#6B7280] font-mono">
                      {new Date(entry.at).toLocaleString()}
                      {entry.auth_code ? ` · auth ${entry.auth_code}` : ""}
                      {entry.kind === "sale_payment" ? ` · balance on ${entry.sale_number || "invoice"}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => discardApproval(entry)}
                      disabled={recovering}
                      data-testid={`discard-tyro-approval-${entry.id}`}
                      className="h-10 px-4 rounded-xl border border-[#E5E7EB] text-[12px] font-semibold text-[#6B7280] hover:border-[#121216] disabled:opacity-50"
                    >
                      Discard
                    </button>
                    <button
                      onClick={() => recoverApproval(entry)}
                      disabled={recovering}
                      data-testid={`record-tyro-approval-${entry.id}`}
                      className="h-10 px-5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-bold disabled:opacity-50"
                    >
                      {recovering ? "Recording…" : "Record this sale"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Mobile tab switcher — phone only */}
          <div className="md:hidden flex gap-1 mb-3 bg-[#F3F4F6] p-1 rounded-xl" data-testid="pos-mobile-tabs">
            <button
              onClick={() => setMobileTab("products")}
              className={`flex-1 h-10 rounded-lg text-[13px] font-semibold transition-all ${mobileTab === "products" ? "bg-white text-[#121216] shadow-sm" : "text-[#9CA3AF]"}`}
              data-testid="pos-tab-products"
            >
              Products
            </button>
            <button
              onClick={() => setMobileTab("cart")}
              className={`flex-1 h-10 rounded-lg text-[13px] font-semibold transition-all relative ${mobileTab === "cart" ? "bg-white text-[#121216] shadow-sm" : "text-[#9CA3AF]"}`}
              data-testid="pos-tab-cart"
            >
              Cart
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-[#1E5FEB] text-white">
                  {cart.reduce((s, x) => s + x.quantity, 0)}
                </span>
              )}
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-3xl bg-white p-16 text-center" style={{ boxShadow: "0 2px 8px rgba(36,71,55,0.04)" }}>
              <Package className="mx-auto mb-4 text-[#9CA3AF]" size={52} weight="duotone" />
              <div className="text-[15px] text-[#4B5563] font-medium">No products yet</div>
              <div className="text-xs text-[#9CA3AF] mt-1">Head to Inventory to add accessories</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-3.5 lg:gap-4" data-testid="pos-product-grid">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} onAdd={addToCart} />
              ))}
            </div>
          )}

          {/* Floating cart badge on mobile when products tab active and cart has items */}
          {mobileTab === "products" && cart.length > 0 && (
            <button
              onClick={() => setMobileTab("cart")}
              className="md:hidden fixed bottom-[84px] right-4 z-[8000] h-14 px-5 rounded-full bg-[#1E5FEB] text-white text-[14px] font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30 active:scale-95 transition-transform"
              data-testid="pos-mobile-cart-fab"
            >
              <Receipt size={18} weight="bold" />
              Cart · {cart.reduce((s, x) => s + x.quantity, 0)} · ${money(total)}
            </button>
          )}
        </section>

        {/* Right: Cart + Tender (floating panel) — on mobile, hidden when products tab is active */}
        <aside className={`md:col-span-5 lg:col-span-5 xl:col-span-4 p-3 md:p-3 lg:p-4 md:pl-0 lg:pl-0 flex flex-col min-w-0 ${mobileTab === "products" ? "hidden md:flex" : ""}`}>
          <div className="w-full flex-1 bg-white rounded-2xl flex flex-col overflow-y-auto md:sticky md:top-3 lg:top-4" style={{ boxShadow: "0 20px 48px rgba(30, 95, 235, 0.10)", maxHeight: "calc(100vh - 24px)" }}>
          {/* Repair ticket lookup */}
          <div className="p-3 bg-[#F8FAFC] border-b border-[#E5E7EB]">
            <div className="overline mb-1.5 flex items-center gap-2">
              <Wrench size={11} weight="bold" /> Repair pickup / settle invoice
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Enter while the dropdown is open & highlighted → pick that row
                if (suggestOpen && suggestions[suggestHighlight]) {
                  pickSuggestion(suggestions[suggestHighlight]);
                } else {
                  addTicketOrInvoice(ticketQ);
                }
              }}
              className="flex gap-2 min-w-0 relative"
            >
              <div className="relative flex-1 min-w-0">
                <input
                  value={ticketQ}
                  onChange={(e) => { setTicketQ(e.target.value); setSuggestOpen(true); }}
                  onFocus={() => setSuggestOpen(true)}
                  onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (!suggestOpen || !suggestions.length) return;
                    if (e.key === "ArrowDown") { e.preventDefault(); setSuggestHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); setSuggestHighlight((h) => Math.max(h - 1, 0)); }
                    else if (e.key === "Escape") { setSuggestOpen(false); }
                  }}
                  placeholder="R-, S- or TI- number (e.g. 45)"
                  className="w-full h-9 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-lg bg-white font-mono"
                  data-testid="pos-ticket-input"
                  autoComplete="off"
                />
                {suggestOpen && ticketQ.trim().length >= 1 && suggestions.length > 0 && (
                  <div
                    className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-[#E5E7EB] overflow-hidden z-50"
                    style={{ boxShadow: "0 14px 32px rgba(30, 95, 235, 0.14)" }}
                    data-testid="pos-ticket-suggest"
                  >
                    {suggestions.map((s, i) => {
                      const isHi = i === suggestHighlight;
                      const tint = s.kind === "invoice"
                        ? "bg-[#DBEAFE] text-[#1E40AF]"
                        : s.kind === "trade_in"
                        ? "bg-[#E0E7FF] text-[#4338CA]"
                        : "bg-[#F3F4F6] text-[#4B5563]";
                      return (
                        <button
                          key={`${s.kind}-${s.id}`}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                          onMouseEnter={() => setSuggestHighlight(i)}
                          data-testid={`pos-suggest-${s.kind}-${s.label}`}
                          className={`w-full text-left px-3 py-2 flex items-center gap-2 border-t border-[#F3F4F6] first:border-t-0 ${isHi ? "bg-[#F8FAFC]" : "bg-white hover:bg-[#F8FAFC]"}`}
                        >
                          <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${tint}`}>
                            {s.kind === "invoice" ? "INVOICE" : "TICKET"}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-mono font-semibold text-[#121216] truncate">{s.label}</div>
                            <div className="text-[10px] text-[#9CA3AF] truncate">{s.customer} · {s.meta}</div>
                          </div>
                          <div className="text-[12px] font-mono font-bold shrink-0 tabular-nums text-right">
                            {s.kind === "invoice" && Number(s.amount || 0) <= 0.005 ? (
                              <span className="text-[#2F8F6E]">PAID</span>
                            ) : (
                              <span className="text-[#121216]">
                                ${Number(s.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={lookingUp || !ticketQ.trim()}
                data-testid="pos-ticket-add"
                className="h-9 px-3 rounded-lg bg-[#121216] hover:bg-[#1E5FEB] disabled:bg-zinc-300 text-white text-xs font-semibold shrink-0"
              >
                {lookingUp ? "…" : "ADD"}
              </button>
            </form>
          </div>

          {/* Customer */}
          <div className="p-3 border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between mb-1.5">
              <div className="overline">CUSTOMER</div>
              {/* Quick action rail — buy-device from within POS so the
                  cashier doesn't have to leave the in-progress cart. This
                  opens the full TradeInIntakeModal; on success the new
                  trade-in's TI-XXXXX can be rung up via the Pickup search
                  above. */}
              <button
                type="button"
                onClick={() => setTradeInOpen(true)}
                className="h-7 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-[#1E5FEB] text-[10.5px] font-semibold uppercase tracking-wider hover:bg-[#EFF6FF] hover:border-[#1E5FEB] inline-flex items-center gap-1"
                data-testid="pos-trade-in-btn"
                title="Buy a used device (Trade-In)"
              >
                <ArrowsLeftRight size={11} weight="bold" />
                Buy Device
              </button>
            </div>
            {customer ? (
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm" data-testid="pos-customer-name">{customer.name}</div>
                    <div className="text-xs font-mono text-zinc-500">{customer.phone || "walk-in"}</div>
                  </div>
                  <button className="text-xs underline" onClick={() => { setCustomer(null); setApplyStoreCredit(false); }}>Change</button>
                </div>
                {/* Unconverted-booking banner — shows when this customer
                    still has a booking with status 'new' or 'confirmed'.
                    One-click: when there's exactly ONE open booking, the
                    button calls /api/bookings/{id}/convert directly and
                    redirects to the freshly-created ticket. When there
                    are multiple, it deep-links to the bookings list so
                    the cashier can pick which one to convert. */}
                {selectedCustomerOpenBookings.length > 0 && (
                  <div
                    className="mt-2 p-2 rounded-lg border-2 border-[#FCD34D] bg-[#FFFBEB]"
                    data-testid="pos-customer-open-bookings-flag"
                  >
                    <div className="flex items-start gap-2">
                      <div className="shrink-0 w-7 h-7 rounded-full bg-[#FCD34D] flex items-center justify-center mt-0.5">
                        <span className="text-[#78350F] text-[14px] leading-none font-black">!</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11.5px] font-bold uppercase tracking-wider text-[#92400E]">
                          {selectedCustomerOpenBookings.length} unconverted booking{selectedCustomerOpenBookings.length === 1 ? "" : "s"}
                        </div>
                        <div className="text-[11px] text-[#78350F] mt-0.5 truncate">
                          Latest: {selectedCustomerOpenBookings[0].device_make || ""} {selectedCustomerOpenBookings[0].device_model || ""}
                          {selectedCustomerOpenBookings[0].device_year ? ` (${selectedCustomerOpenBookings[0].device_year})` : ""}
                          {selectedCustomerOpenBookings[0].device_model_number ? ` · ${selectedCustomerOpenBookings[0].device_model_number}` : ""}
                          {selectedCustomerOpenBookings[0].problem ? ` · ${selectedCustomerOpenBookings[0].problem}` : ""}
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            // One-click path: ONE open booking → call the
                            // convert endpoint immediately, navigate to the
                            // new ticket. The booking auto-flips to
                            // 'converted' so the flag disappears next refresh.
                            if (selectedCustomerOpenBookings.length === 1) {
                              const b = selectedCustomerOpenBookings[0];
                              try {
                                const r = await api.post(`/bookings/${b.id}/convert`, {});
                                toast.success(`Booking converted → ${r.data.ticket_number || "ticket created"}`);
                                if (r.data?.ticket_id) {
                                  nav(`/repairs/${r.data.ticket_id}`);
                                } else {
                                  nav(`/repairs`);
                                }
                              } catch (e) {
                                toast.error(formatApiError(e));
                              }
                              return;
                            }
                            // Multiple bookings → fall back to picker.
                            nav(`/bookings?customer_id=${customer.id}&status=new`);
                          }}
                          className="mt-1.5 h-7 px-3 rounded-full bg-[#92400E] text-white text-[11px] font-semibold hover:bg-[#78350F] transition-colors"
                          data-testid="pos-convert-booking-btn"
                        >
                          {selectedCustomerOpenBookings.length === 1 ? "Convert this booking" : `Pick from ${selectedCustomerOpenBookings.length} bookings`}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Store-credit redemption. When the customer has a positive
                    balance we surface a single-click toggle so the cashier
                    can opt in without leaving the cart. On submit the sale
                    endpoint caps redemption at min(balance, total) and
                    decrements the balance atomically — the display here
                    shows the *effective* redemption so the cashier sees
                    exactly what will be applied. */}
                {creditBalance > 0.005 && (
                  <div
                    className={`mt-2 flex items-center gap-2 p-2.5 rounded-lg border-2 transition-colors ${applyStoreCredit ? "border-emerald-600 bg-emerald-50" : "border-emerald-200 bg-white"}`}
                    data-testid="pos-store-credit-card"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-800">Store credit</div>
                      <div className="text-[15px] font-bold text-emerald-900 tabular-nums" data-testid="pos-store-credit-balance">
                        ${money(creditBalance)}
                        {applyStoreCredit && <span className="ml-1.5 text-[11px] font-medium text-emerald-700">· using ${money(creditApplied)}</span>}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setApplyStoreCredit((v) => !v)}
                      className={`h-8 px-3 rounded-full text-[11px] font-semibold transition-colors ${applyStoreCredit ? "bg-white border border-emerald-600 text-emerald-800 hover:bg-emerald-100" : "bg-emerald-700 text-white hover:bg-emerald-800"}`}
                      data-testid="pos-use-store-credit-btn"
                    >
                      {applyStoreCredit ? "Remove" : "Use credit"}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative">
                <div className="flex gap-2 min-w-0">
                  <input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search or leave as walk-in"
                    className="flex-1 min-w-0 h-10 px-3 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-sm rounded-none"
                    data-testid="pos-customer-search"
                  />
                  <button onClick={createWalkIn} className="h-10 px-3 border border-[#121216] text-xs font-semibold hover:bg-[#121216] hover:text-white shrink-0">
                    <User size={14} weight="bold" />
                  </button>
                </div>
                {customers.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 border border-[#E5E7EB] bg-white z-10 max-h-60 overflow-auto">
                    {customers.map((c) => {
                      const openCount = (openBookingsByCustomer[c.id] || []).length;
                      return (
                        <button
                          key={c.id}
                          onClick={() => { setCustomer(c); setCustomerSearch(""); setCustomers([]); }}
                          className="w-full text-left p-2 text-sm hover:bg-[#F8FAFC] border-b border-[#E5E7EB] last:border-0"
                          data-testid={`pos-customer-result-${c.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{c.name}</div>
                              <div className="text-xs text-zinc-500 font-mono">{c.phone}</div>
                            </div>
                            {openCount > 0 && (
                              <span
                                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-[10.5px] font-bold uppercase tracking-wider border border-[#FCD34D]"
                                title={`${openCount} unconverted booking${openCount === 1 ? "" : "s"}`}
                                data-testid={`pos-customer-booking-flag-${c.id}`}
                              >
                                {openCount} booking{openCount === 1 ? "" : "s"}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cart */}
          <div className="flex-1 overflow-auto min-h-0" data-testid="pos-cart">
            {cart.length === 0 ? (
              <div className="p-6 text-center text-xs text-zinc-400">Cart is empty. Tap a product.</div>
            ) : (
              cart.map((it, idx) => (
                <div key={idx} className={`px-4 py-3 border-b border-[#E5E7EB] ${it.kind === "repair" ? "bg-[#1E5FEB]/5 border-l-2 border-l-[#1E5FEB]" : ""}`} data-testid={`cart-item-${idx}`}>
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium truncate flex items-center gap-1.5">
                        {it.kind === "repair" && <Wrench size={12} weight="bold" className="text-[#1E5FEB]" />}
                        {it.name}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                        {it.kind === "repair" ? `TICKET ${it.ticket_number || it.sku}` : it.sku}
                      </div>
                    </div>
                    <button onClick={() => removeItem(idx)} className="text-zinc-400 hover:text-[#DC2626] p-1 shrink-0">
                      <Trash size={14} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    {it.kind === "repair" ? (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 bg-[#1E5FEB] text-white font-semibold rounded">BALANCE DUE</span>
                    ) : (
                      <div className="flex items-center border border-[#E5E7EB] rounded">
                        <button onClick={() => changeQty(idx, -1)} className="w-8 h-8 hover:bg-[#F8FAFC]"><Minus size={12} /></button>
                        <div className="w-9 text-center font-mono font-bold text-[13px]">{it.quantity}</div>
                        <button onClick={() => changeQty(idx, 1)} className="w-8 h-8 hover:bg-[#F8FAFC]"><Plus size={12} /></button>
                      </div>
                    )}
                    {priceEdit && priceEdit.idx === idx ? (
                      <div className="flex items-center gap-1">
                        <span className="text-[12px] font-mono text-[#9CA3AF]">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          autoFocus
                          value={priceEdit.value}
                          onChange={(e) => setPriceEdit({ idx, value: e.target.value })}
                          onBlur={commitPriceEdit}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commitPriceEdit(); }
                            if (e.key === "Escape") { e.preventDefault(); setPriceEdit(null); }
                          }}
                          data-testid={`cart-price-input-${idx}`}
                          className="w-24 h-8 px-2 border border-[#1E5FEB] rounded font-mono font-bold text-[14px] text-right tabular-nums bg-white focus:outline-none focus:ring-2 focus:ring-[#1E5FEB]/20"
                        />
                        {it.quantity > 1 && (
                          <span className="text-[10px] text-[#9CA3AF] font-mono whitespace-nowrap">/ ea</span>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPriceEdit({ idx, value: String(it.unit_price) })}
                        data-testid={`cart-price-${idx}`}
                        title="Click to edit price"
                        className="group flex items-baseline gap-1 font-mono font-bold text-[14px] tabular-nums hover:text-[#1E5FEB] transition-colors cursor-text"
                      >
                        <span className="border-b border-dashed border-transparent group-hover:border-[#1E5FEB]/40">
                          ${money(it.unit_price * it.quantity)}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Tender */}
          <div className="border-t border-[#E5E7EB] p-4 space-y-3 bg-[#F8FAFC]">
            <div className="flex items-center justify-between">
              <div className="overline">
                Subtotal
                {taxInclusive && taxRate > 0 && (
                  <span className="ml-1 text-[9px] text-[#9CA3AF] normal-case tracking-normal">incl. tax</span>
                )}
              </div>
              <div className="font-display text-[14px] font-medium tabular-nums">${money(subtotal)}</div>
            </div>
            <div className="flex items-center justify-between">
              <label className="overline" htmlFor="pos-discount">Discount</label>
              <input
                id="pos-discount"
                type="number" min="0" step="0.01"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="w-24 h-8 px-2 text-right font-mono text-sm tabular-nums border border-[#E5E7EB] bg-white rounded-lg focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 transition-all"
                data-testid="pos-discount"
              />
            </div>
            {taxRate > 0 && (
              <div className="flex items-center justify-between">
                <div className="overline">
                  {taxInclusive ? "Tax incl." : "Tax"} <span className="text-[9px] text-[#9CA3AF] normal-case tracking-normal">{(taxRate * 100).toFixed(taxRate * 100 % 1 === 0 ? 0 : 2)}%</span>
                </div>
                <div className="font-mono text-xs text-[#4B5563] tabular-nums" data-testid="pos-tax">
                  {taxInclusive ? "" : "+ "}${money(taxAmount)}
                </div>
              </div>
            )}
            {creditApplied > 0 && (
              <div className="flex items-center justify-between" data-testid="pos-credit-applied-row">
                <div className="overline text-emerald-700">Store credit</div>
                <div className="font-mono text-sm text-emerald-700 tabular-nums">-${money(creditApplied)}</div>
              </div>
            )}
            <div className="flex items-baseline justify-between pt-3 border-t border-[#E5E7EB]">
              <div className="font-display text-[#4B5563] text-xs font-medium tracking-wide uppercase">Total</div>
              <div className="font-display font-semibold text-[32px] leading-none text-[#121216] tracking-tight tabular-nums" data-testid="pos-total">${money(total)}</div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="overline">Payment method</div>
                <button
                  type="button"
                  onClick={() => {
                    const next = !splitMode;
                    setSplitMode(next);
                    if (next) {
                      // Pre-seed from the single-tender field if present.
                      const cur = Number(tendered || 0);
                      setSplitCash(cur > 0 ? String(cur.toFixed(2)) : "");
                      setSplitCard(""); setSplitOther("");
                    } else {
                      setSplitCash(""); setSplitCard(""); setSplitOther("");
                    }
                  }}
                  data-testid="pos-split-toggle"
                  className={`text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full transition-colors border ${
                    splitMode
                      ? "border-[#1E5FEB] bg-[#1E5FEB] text-white"
                      : "border-[#E5E7EB] text-[#4B5563] hover:border-[#1E5FEB] hover:text-[#1E5FEB]"
                  }`}
                  title="Pay with multiple methods on one ticket"
                >
                  {splitMode ? "Split on" : "Split"}
                </button>
              </div>
              {!splitMode && (() => {
                // Show TYRO as a 4th button when the store has Tyro enabled.
                // The integration_key is fetched at click time from
                // `/stores/{id}/tyro/sdk-key`; if it's missing the dialog
                // surfaces the error then. Hiding the button instead would
                // make merchants think Tyro isn't installed at all.
                const tyroOn = !!currentStore?.tyro_enabled;
                const methods = tyroOn
                  ? ["cash", "card_manual", "tyro", "other"]
                  : ["cash", "card_manual", "other"];
                return (
                  <div className={`grid gap-2 ${tyroOn ? "grid-cols-4" : "grid-cols-3"}`}>
                    {methods.map((m) => {
                      if (m === "tyro") {
                        return (
                          <button
                            key="tyro"
                            data-testid="pos-pay-tyro"
                            onClick={async () => {
                              if (cart.length === 0) return toast.error("Cart is empty");
                              if (total <= 0) return toast.error("Total must be greater than $0");
                              if (isDeposit && !window.confirm(
                                `Charge a part-payment of $${tenderedNum.toFixed(2)} on Tyro?\n\nBalance owing: $${balanceDue.toFixed(2)}\n\nClear "Amount tendered" to charge the full $${total.toFixed(2)}.`
                              )) return;
                              if (await precheckCart()) setTyroOpen(true);
                            }}
                            className="h-10 rounded-lg text-[12px] tracking-wider font-semibold transition-all active:scale-[0.97] bg-white border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white shadow-[0_2px_8px_rgba(30,95,235,0.15)] hover:shadow-[0_4px_14px_rgba(30,95,235,0.3)] flex items-center justify-center gap-1.5 group"
                            title="Charge to a paired Tyro EFTPOS terminal"
                          >
                            <img
                              src="/tyro-logo.gif"
                              alt="Tyro"
                              className="h-4 w-auto group-hover:brightness-0 group-hover:invert transition-all"
                            />
                            <span className="text-[11px]">EFTPOS</span>
                          </button>
                        );
                      }
                      return (
                        <button
                          key={m}
                          data-testid={`pos-pay-${m}`}
                          onClick={() => setPaymentMethod(m)}
                          className={`h-10 rounded-lg text-[12px] tracking-wider font-semibold transition-all active:scale-[0.97] ${paymentMethod === m ? "bg-[#1E5FEB] text-white shadow-[0_4px_14px_rgba(30,95,235,0.25)]" : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#1E5FEB] hover:text-[#1E5FEB]"}`}
                        >
                          {m === "card_manual" ? "CARD" : m.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {!splitMode ? (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="overline">Amount tendered</div>
                  <button
                    type="button"
                    onClick={() => setTendered(total > 0 ? String(total.toFixed(2)) : "")}
                    data-testid="pos-tender-exact"
                    disabled={cart.length === 0 || total <= 0}
                    className="text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#1E5FEB]"
                  >
                    Exact
                  </button>
                </div>
                <input
                  type="number" min="0" step="0.01"
                  value={tendered}
                  onChange={(e) => setTendered(e.target.value)}
                  placeholder="0.00"
                  className="w-full h-11 px-4 font-display font-medium text-lg text-right border border-[#E5E7EB] bg-white rounded-lg focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 transition-all"
                  data-testid="pos-tendered"
                />
                {/* Quick tender chips */}
                <div className="grid grid-cols-4 gap-1.5 mt-2">
                  {[20, 50, 100, 200].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setTendered(String(amt))}
                      disabled={cart.length === 0}
                      data-testid={`pos-tender-${amt}`}
                      className="h-9 rounded-md bg-white border border-[#E5E7EB] text-[11.5px] font-mono font-semibold text-[#4B5563] tabular-nums hover:border-[#1E5FEB] hover:text-[#1E5FEB] hover:bg-[#EFF6FF] transition-colors disabled:opacity-40 disabled:hover:bg-white disabled:hover:border-[#E5E7EB] disabled:hover:text-[#4B5563]"
                    >
                      ${amt}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="overline">Split tender</div>
                  <button
                    type="button"
                    onClick={() => {
                      // Auto-fill: top up the empty rows so the sum equals total.
                      // Prefer Tyro if available (matches "customer pays rest with
                      // card" retail flow), otherwise CARD, then OTHER.
                      const remaining = Math.max(0, total - splitCashNum - splitCardNum - splitTyroNum - splitOtherNum);
                      if (remaining <= 0) return;
                      const tyroOn = !!currentStore?.tyro_enabled;
                      if (tyroOn && !splitTyro) setSplitTyro(remaining.toFixed(2));
                      else if (!splitCard && !splitOther) setSplitCard(remaining.toFixed(2));
                      else if (!splitOther) setSplitOther(remaining.toFixed(2));
                      else setSplitCash((splitCashNum + remaining).toFixed(2));
                    }}
                    data-testid="pos-split-balance"
                    disabled={cart.length === 0}
                    className="text-[10px] font-semibold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border border-[#1E5FEB] text-[#1E5FEB] hover:bg-[#1E5FEB] hover:text-white transition-colors disabled:opacity-40"
                  >
                    Auto-balance
                  </button>
                </div>
                {[
                  { key: "cash", label: "CASH", val: splitCash, set: setSplitCash, show: true },
                  { key: "card_manual", label: "CARD", val: splitCard, set: setSplitCard, show: true },
                  { key: "tyro", label: "TYRO", val: splitTyro, set: setSplitTyro,
                    show: !!currentStore?.tyro_enabled,
                    hint: "Charged via EFTPOS terminal on checkout" },
                  { key: "other", label: "OTHER", val: splitOther, set: setSplitOther, show: true },
                ].filter((r) => r.show).map((row) => (
                  <div key={row.key} className="flex items-center gap-2">
                    <div className="w-[68px] text-[11px] tracking-wider font-semibold text-[#4B5563] uppercase shrink-0 flex items-center gap-1">
                      {row.label}
                      {row.key === "tyro" && (
                        <img src="/tyro-logo.gif" alt="" className="h-3 w-auto" />
                      )}
                    </div>
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] font-mono text-[13px]">$</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={row.val}
                        onChange={(e) => row.set(e.target.value)}
                        placeholder="0.00"
                        data-testid={`pos-split-${row.key}`}
                        title={row.hint || ""}
                        className="w-full h-10 pl-7 pr-3 font-display font-medium text-right border border-[#E5E7EB] bg-white rounded-lg focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 transition-all tabular-nums"
                      />
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between text-[11px] font-mono text-[#4B5563] pt-1">
                  <span>Sum tendered</span>
                  <span data-testid="pos-split-sum" className="tabular-nums font-semibold text-[#121216]">
                    ${money(splitTotal)}
                    {Math.round(splitTotal * 100) === totalCents && total > 0 && (
                      <span className="ml-1.5 text-[#15803D]">✓</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            {isDeposit ? (
              <div
                className="flex items-center justify-between rounded-lg px-3 py-2.5 border"
                style={{
                  background: "linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)",
                  borderColor: "#DC2626",
                  boxShadow: "0 0 0 3px rgba(220, 38, 38, 0.08)",
                }}
                data-testid="pos-balance-due-card"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#DC2626] font-bold">Balance due</div>
                  <div className="text-[10px] font-mono text-[#7F1D1D] mt-0.5">Deposit · ${money(tenderedNum)}</div>
                </div>
                <div className="font-display font-bold text-[20px] text-[#DC2626] tabular-nums" data-testid="pos-balance-due-amount">
                  ${money(balanceDue)}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-[#DBEAFE] rounded-lg px-3 py-2.5">
                <div className="overline text-[#1E5FEB]">Change due</div>
                <div className="font-display font-semibold text-[18px] text-[#1E5FEB] tabular-nums">${money(change)}</div>
              </div>
            )}

            {/* Optional cashier note — prints on every receipt (PDF, thermal,
                public HTML). Used for things like "gift", "warranty claim",
                serial number, in-store credit reason, etc. */}
            <div>
              <label
                htmlFor="pos-sale-note"
                className="overline flex items-center justify-between"
              >
                <span>Note on receipt</span>
                <span className="text-[9px] font-mono text-[#9CA3AF] normal-case tracking-normal" data-testid="pos-sale-note-count">
                  {saleNote.length}/140
                </span>
              </label>
              <textarea
                id="pos-sale-note"
                name="pos-sale-note"
                data-testid="pos-sale-note"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                autoComplete="off"
                spellCheck="true"
                rows={2}
                maxLength={140}
                value={saleNote}
                onChange={(e) => setSaleNote(e.target.value.slice(0, 140))}
                placeholder="e.g. Birthday gift · warranty claim · S/N 12345"
                className="mt-1.5 w-full text-[12.5px] border border-[#E5E7EB] rounded-lg px-3 py-2 leading-snug focus:border-[#1E5FEB] focus:outline-none focus:ring-4 focus:ring-[#1E5FEB]/10 transition-all resize-none placeholder:text-[#9CA3AF]"
              />
            </div>

            <button
              onClick={() => checkout()}
              disabled={
                submitting ||
                cart.length === 0 ||
                (splitMode
                  // Split mode — allow partial split (deposit) so the
                  // cert `Retail.SplitPurchase.1` flow works:
                  //   1) Cash $10 + Tyro $102.50 = $112.50 on a $215 sale → Take Deposit
                  //   2) Then second Tyro $102.50 on Sale Detail clears the balance.
                  // Only blockers are: zero tendered, OR overpayment (>total).
                  ? (total > 0 && (splitTotal <= 0 || splitTotal > total + 0.01))
                  : (total > 0 && tenderedCents <= 0))
              }
              data-testid="pos-checkout"
              className={`w-full h-14 disabled:bg-[#E5E7EB] disabled:text-[#9CA3AF] disabled:cursor-not-allowed text-white font-display font-semibold text-[16px] tracking-tight transition-all active:scale-[0.98] flex items-center justify-center gap-2 rounded-xl ${
                isDeposit
                  ? "bg-[#DC2626] hover:bg-[#B91C1C] shadow-[0_8px_24px_rgba(220,38,38,0.28)]"
                  : "bg-[#1E5FEB] hover:bg-[#1849C8] shadow-[0_8px_24px_rgba(30,95,235,0.28)]"
              }`}
            >
              {submitting ? (
                <>
                  <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                    <path d="M21 12a9 9 0 0 1-9 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  </svg>
                  PROCESSING…
                </>
              ) : (
                <><Receipt size={18} weight="bold" /> {isDeposit ? "Take Deposit" : "Complete Sale"}</>
              )}
            </button>
          </div>

          {lastSale && (
            <div className="border-t border-[#E5E7EB] px-4 py-3 bg-white">
              <div className="flex items-center justify-between gap-2">
                <div className="overline truncate">LAST · {lastSale.sale_number}</div>
                <button
                  onClick={() => setPostSaleOpen(true)}
                  data-testid="reopen-post-sale"
                  className="h-9 px-3 rounded-lg bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-semibold flex items-center gap-1.5 transition-colors active:scale-[0.98]"
                >
                  <PaperPlaneTilt size={12} weight="bold" /> Send / Print
                </button>
              </div>
            </div>
          )}
          </div>
        </aside>
      </div>

      {lastSale && <ReceiptPrint sale={lastSale} />}
      {lastSaleTicket && <LabelPrint ticket={lastSaleTicket} />}

      {/* Tyro EFTPOS dialog — drives a real terminal transaction, then kicks
          checkout(tyroMeta) with the SDK result so the sale records the auth
          code, RRN, card scheme & elided PAN for refund linking. After
          checkout resolves, `lastSale` is populated and the dialog morphs
          its body into the unified SaleCompleteCard action panel — no
          second pop-up. The cashier taps Done in the inline panel to close. */}
      {tyroOpen && (
        <TyroPaymentDialog
          mode="purchase"
          amountCents={Math.round(tyroChargeAmount * 100)}
          onClose={() => { setTyroOpen(false); setPostSaleOpen(false); setTyroSaveError(null); }}
          onApproved={async (meta) => {
            await checkout(meta);
            // Don't close — the dialog will morph to SAVED via
            // `completedSale` below the moment lastSale is populated.
          }}
          completedSale={lastSale}
          saveError={tyroSaveError}
          onRetrySave={retryTyroSave}
          customer={lastSaleCustomer}
          repairTicket={lastSaleTicket}
          storeForActions={currentStore}
          onPrintReceipt={printReceipt}
          onPrintLabel={printLabel}
          onViewInvoice={() => lastSale && nav(`/sales/${lastSale.id}`)}
        />
      )}

      {/* Split-tender Tyro dialog — charges ONLY the Tyro portion of a
          cash+Tyro split. On approval we re-enter checkout with the meta
          which is attached to the 'tyro' row in payments[]. On approval +
          save the dialog morphs into the SaleCompleteCard. On close/decline
          before approval, the sale is NOT saved (cancel-whole-sale policy). */}
      {tyroSplitOpen && (
        <TyroPaymentDialog
          mode="purchase"
          amountCents={Math.round(splitTyroNum * 100)}
          onClose={() => {
            // Cashier cancelled — release the in-flight lock so they
            // can retry without reloading the page.
            setTyroSplitOpen(false);
            checkoutInFlight.current = false;
            setTyroSaveError(null);
          }}
          onApproved={async (meta) => {
            // The lock is still held here. checkout(meta) will see
            // checkoutInFlight=true and skip its early-return, then the
            // finally{} block clears it.
            checkoutInFlight.current = false;
            await checkout(meta);
            // Keep dialog open — morphs to SAVED via `completedSale`.
          }}
          completedSale={lastSale}
          saveError={tyroSaveError}
          onRetrySave={retryTyroSave}
          customer={lastSaleCustomer}
          repairTicket={lastSaleTicket}
          storeForActions={currentStore}
          onPrintReceipt={printReceipt}
          onPrintLabel={printLabel}
          onViewInvoice={() => lastSale && nav(`/sales/${lastSale.id}`)}
        />
      )}

      {/* Unified Sale Complete card — used as a standalone overlay for
          non-Tyro payment methods (cash / card / other / split-without-
          Tyro). For Tyro flows the same card body renders INSIDE the
          TyroPaymentDialog above (mode="inline") — single source of
          truth, two presentation surfaces. */}
      <SaleCompleteCard
        open={postSaleOpen}
        onClose={() => setPostSaleOpen(false)}
        sale={lastSale}
        customer={lastSaleCustomer}
        store={currentStore}
        repairTicket={lastSaleTicket}
        onPrintReceipt={printReceipt}
        onPrintLabel={printLabel}
        onViewInvoice={() => lastSale && nav(`/sales/${lastSale.id}`)}
      />

      <AddCustomerModal
        open={addCustomerOpen}
        onClose={() => setAddCustomerOpen(false)}
        initialPhone={customerSearch}
        onCreated={(c) => { setCustomer(c); setCustomerSearch(""); setCustomers([]); }}
        onSelectExisting={(c) => { setCustomer(c); setCustomerSearch(""); setCustomers([]); toast.success(`Using ${c.name}`); }}
      />

      {/* Trade-In intake — opened from the sidebar rail so cashiers can buy
          a used device mid-checkout without leaving the POS. Reuses the
          same modal shown on /trade-ins, and on success adds the newly-
          created trade-in product to the current cart so the cashier can
          ring it up immediately if the customer is selling+upgrading. */}
      {tradeInOpen && (
        <TradeInIntakeModal
          onClose={() => setTradeInOpen(false)}
          onCreated={(ti) => {
            setTradeInOpen(false);
            loadProducts();
            toast.success(`${ti.trade_in_number} added to inventory · type it in Pickup search to sell`);
          }}
        />
      )}

      {/* Invoice Take-Payment modal — opens when operator types an S-xxxxx into
          the Repair Pickup field. Lets them settle the overdue balance without
          leaving the POS. */}
      {invoicePay.open && invoicePay.invoice && (() => {
        const inv = invoicePay.invoice;
        const balance = Math.max(0, (inv.total || 0) - (inv.amount_tendered || 0));
        return (
          <div
            className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !invoicePay.submitting && setInvoicePay({ open: false, invoice: null, amount: "", method: "cash", submitting: false })}
            data-testid="pos-invoice-pay-modal"
          >
            <div
              className="bg-white w-full max-w-md rounded-[24px] shadow-2xl border border-slate-200/60 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 pt-6 pb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">TAKE PAYMENT</div>
                <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight mt-0.5">Settle balance on {inv.sale_number}</h3>
                <div className="text-[12px] text-slate-500 mt-0.5">
                  {inv.customer_name || "Walk-in"} · total ${money(inv.total)}
                </div>
                <div className="mt-3 rounded-xl border-2 px-3 py-2 flex items-baseline justify-between"
                     style={{ background: "linear-gradient(135deg, #FEE2E2 0%, #FEF2F2 100%)", borderColor: "#DC2626" }}>
                  <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#DC2626]">Outstanding balance</span>
                  <span className="font-mono font-bold text-[20px] text-[#DC2626] tabular-nums">${money(balance)}</span>
                </div>
              </div>
              <div className="px-6 pb-6 space-y-3">
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Amount</div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-mono">$</span>
                    <input
                      type="number" min="0" step="0.01" max={balance}
                      value={invoicePay.amount}
                      onChange={(e) => setInvoicePay((p) => ({ ...p, amount: e.target.value }))}
                      className="w-full h-12 pl-7 pr-3 rounded-xl border border-slate-200 focus:border-[#DC2626] focus:ring-2 focus:ring-red-100 focus:outline-none text-lg font-mono font-semibold"
                      data-testid="pos-invoice-pay-amount"
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <button type="button" onClick={() => setInvoicePay((p) => ({ ...p, amount: balance.toFixed(2) }))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-slate-700">EXACT</button>
                    {[20, 50, 100].map((v) => (
                      <button key={v} type="button" onClick={() => setInvoicePay((p) => ({ ...p, amount: String(Math.min(v, balance)) }))} className="flex-1 h-8 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-mono font-semibold text-slate-700">${v}</button>
                    ))}
                  </div>
                </label>
                <label className="block">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Method</div>
                  <div className="grid grid-cols-4 gap-2">
                    {["cash", "card_manual", "tyro", "other"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setInvoicePay((p) => ({ ...p, method: m }))}
                        data-testid={`pos-invoice-pay-method-${m}`}
                        className={`h-10 rounded-lg text-[11px] tracking-wider font-semibold transition-all flex items-center justify-center gap-1.5 ${
                          invoicePay.method === m
                            ? "bg-[#121216] text-white"
                            : "bg-white border border-[#E5E7EB] text-[#4B5563] hover:border-[#121216]"
                        }`}
                      >
                        {m === "tyro" ? (
                          <>
                            <span>TYRO</span>
                            <img
                              src="/tyro-logo.gif"
                              alt=""
                              className={`h-3 w-auto ${invoicePay.method === m ? "invert brightness-0" : ""}`}
                              style={invoicePay.method === m ? { filter: "invert(1) brightness(2)" } : undefined}
                            />
                          </>
                        ) : m === "card_manual" ? "CARD" : m.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </label>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    disabled={invoicePay.submitting}
                    onClick={() => setInvoicePay({ open: false, invoice: null, amount: "", method: "cash", submitting: false })}
                    className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold disabled:opacity-50"
                  >CANCEL</button>
                  <button
                    type="button"
                    onClick={() => submitInvoicePayment()}
                    disabled={invoicePay.submitting || !(Number(invoicePay.amount) > 0)}
                    data-testid="pos-invoice-pay-confirm"
                    className="flex-1 h-11 rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-200 disabled:text-slate-400 text-white text-[13px] font-semibold shadow-md shadow-red-500/25"
                  >
                    {invoicePay.submitting ? "RECORDING…" : "RECORD PAYMENT"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      {/* Tyro EFTPOS dialog for invoice settle */}
      {tyroInvOpen && (
        <TyroPaymentDialog
          mode="purchase"
          amountCents={Math.round(Number(invoicePay.amount || 0) * 100)}
          onClose={() => setTyroInvOpen(false)}
          onApproved={async (meta) => {
            await submitInvoicePayment(meta);
            setTyroInvOpen(false);
          }}
        />
      )}
    </Layout>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <div className={`overline ${strong ? "text-[#121216]" : ""}`}>{label}</div>
      <div className={`font-mono ${strong ? "font-bold text-base" : "text-sm"}`}>{value}</div>
    </div>
  );
}

// Compact "Till" status bar in the POS header. Two affordances:
//   1. The status pill — colour-coded (green when shift open, yellow when not).
//      Click → /till for the full dashboard.
//   2. A direct "Cash out" button — only visible while a shift is open, opens
//      the shared CashOutDialog right inside POS so staff don't have to leave
//      the checkout flow to record a drop / deposit / petty-cash payout.
// Polls /shifts/active every 60s so the displayed expected cash stays roughly
// fresh without hammering the backend.
function TillStatusPill() {
  const [shift, setShift] = useState(null);
  const [expected, setExpected] = useState(0);
  const [showCashOut, setShowCashOut] = useState(false);

  const fetchActive = useRef(null);
  fetchActive.current = async () => {
    try {
      const r = await api.get("/shifts/active");
      setShift(r.data?.shift || null);
      setExpected(Number(r.data?.expected_cash || 0));
    } catch {
      setShift(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const f = async () => {
      if (cancelled) return;
      await fetchActive.current?.();
    };
    f();
    const t = setInterval(f, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <>
      <div className="flex items-center gap-1.5">
        {shift ? (
          <Link
            to="/till"
            data-testid="pos-till-pill-open"
            className="inline-flex items-center gap-2 h-11 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[#121216] text-[12px] font-semibold hover:border-[#1E5FEB] hover:text-[#1E5FEB] transition-colors"
            title={`Shift opened by ${shift.opened_by_name}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#0F9D58] animate-pulse" />
            <Wallet size={13} weight="bold" className="text-[#1E5FEB]" />
            <span>Till</span>
            <span className="font-mono tabular-nums text-[12px]">${money(expected)}</span>
          </Link>
        ) : (
          <Link
            to="/till"
            data-testid="pos-till-pill-closed"
            className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl border border-[#E5E7EB] bg-[#FEF3C7] text-[#92400E] text-[12px] font-semibold hover:border-[#92400E]"
            title="No shift in progress — open one"
          >
            <LockOpen size={13} weight="bold" />
            <span>No shift</span>
          </Link>
        )}

        <button
          type="button"
          onClick={() => setShowCashOut(true)}
          data-testid="pos-cash-out-btn"
          className="inline-flex items-center gap-1.5 h-11 px-3 rounded-xl border border-[#E5E7EB] bg-white text-[#DC2626] text-[12px] font-semibold hover:border-[#DC2626] hover:bg-[#FEE2E2] transition-colors"
          title={shift ? "Record a cash payout from the drawer" : "Open a shift first to record cash-outs"}
        >
          <ArrowUpRight size={13} weight="bold" />
          Cash out
        </button>
      </div>

      {showCashOut && (
        <CashOutDialog
          noActiveShift={!shift}
          onClose={() => setShowCashOut(false)}
          onSaved={() => {
            setShowCashOut(false);
            fetchActive.current?.();
          }}
        />
      )}
    </>
  );
}
